import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { MatchAccessibility, PlatformType } from '@okletsplay/core';
import { MatchPlayerStatus } from '@okletsplay/core/src/modules/match/enums';
import { MATCH_ID_QUERY_KEY } from 'app/constants';
import { ModalName, Route } from 'app/enums';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { MatchEntity } from 'app/interfaces/entity';
import { modalsSelectorFactory } from 'app/store/modal/selectors';
import { modalsReducers } from 'app/store/modal/slice';
import { sessionSelector } from 'app/store/session/selectors';
import { dispatchUnwrap } from 'app/utils/store';
import { gamesSelector, gamesSelectorFactory } from 'modules/games/store/games/selectors';
import { MatchAccordionDescription } from 'modules/matches/components/MatchAccordionDescription';
import { MatchPanelType } from 'modules/matches/enums';
import { useCreateMatchForm } from 'modules/matches/hooks/use-create-match-form';
import { MatchStoreStatus } from 'modules/matches/store/matches/enums';
import { createMatch } from 'modules/matches/store/matches/repository';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { walletSelector } from 'modules/payments/store/wallet/selectors';
import { FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Accordion } from 'shared/components/Accordion';
import { WithPaidActionButton } from 'modules/game-payments/components';
import { GameSelection } from 'shared/components/Form/GameSelection';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { usePlatformLinkAction } from 'shared/hooks';
import { ActionGuard } from 'shared/components/ActionGuard';
import { MatchPlayersForm } from './MatchPlayersForm';
import { MatchPaymentForm } from './MatchPaymentForm';
import { MatchSetupSettingsForm } from './MatchSetupSettingsForm';
import { MatchSummaryForm } from './MatchSummaryForm';
import { MatchFormFields } from 'modules/matches/interfaces/forms';
import { GameActivityType } from 'shared/enums';
import {
  GameActivityPaymentContext,
  GameActivityPaymentProvider,
} from 'modules/game-payments/providers';
import { MultiCurrencyConfig } from 'config/multi-currency-config';

const MODAL_TITLE = 'CREATE A MATCH';

interface CreateMatchModalComponentProps {
  setGameId: (id: string) => void;
  gameId: string;
}

const CreateMatchModalComponent: FC<CreateMatchModalComponentProps> = ({ setGameId, gameId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { paymentConfig, platformFeePercentage } = useContext(GameActivityPaymentContext);

  const game = useSelector(gamesSelectorFactory.useById(gameId));
  const games = useSelector(gamesSelector.matchGames);

  const balance = useSelector(walletSelector.balance);
  const userAccounts = useSelector(userPaymentAccountsSelector.all);
  const user = useSelector(sessionSelector.activeUser);
  const matchPreset: Partial<MatchEntity> = useSelector(
    modalsSelectorFactory.useModalPayload(ModalName.CreateMatch)
  );
  const [expanded, setExpanded] = useState<MatchPanelType | null>(MatchPanelType.GameSelection);

  const { form, defaultValues } = useCreateMatchForm(game, {
    getUserAccounts: () => userAccounts,
    getExchangeRate: () => paymentConfig.exchangeRate,
    getPlatformFeePercentage: () => platformFeePercentage,
  });

  const {
    reset,
    setValue,
    getValues,
    formState: { isValid, isValidating },
    reset: resetForm,
    register,
    unregister,
  } = form;

  useEffect(() => {
    resetForm(getValues());
  }, [
    resetForm,
    getValues,
    balance,
    userAccounts,
    paymentConfig.exchangeRate,
    platformFeePercentage,
  ]);

  const { openPlatformLink, isPlatformLinked } = usePlatformLinkAction(game?.platformType);

  const creating = useEntityRepositoryStatus({
    [EntityStoreName.Matches]: MatchStoreStatus.Creating,
  });

  const opponents = useMemo(() => {
    return matchPreset?.players
      ?.filter(
        (player) =>
          player?.userId !== user.userId &&
          [MatchPlayerStatus.Played, MatchPlayerStatus.Ready, MatchPlayerStatus.Invited].includes(
            player.status
          )
      )
      .map((player) => player.user);
  }, [matchPreset?.players, user.userId]);

  const handleAccordionChange = useCallback(
    (panel: MatchPanelType) => {
      setExpanded((state) => (panel === state ? null : panel));
    },
    [setExpanded]
  );

  const matchToUpdate = useMemo(() => {
    const defaultFormValues: MatchFormFields = {
      accessibility: matchPreset?.accessibility ?? MatchAccessibility.Public,
      isJoinApproveRequired: matchPreset?.isJoinApproveRequired ?? true,
      gameId: matchPreset?.game?.id ?? games[0]?.id,
      entryFee: matchPreset?.entryFee ?? 0,
      players: opponents?.length ? opponents.map((opponent) => opponent.userId) : [],
    };

    const platformType = matchPreset?.game?.platformType;
    if (platformType && [PlatformType.Lichess, PlatformType.Lidraughts].includes(platformType)) {
      return {
        ...defaultFormValues,
        clockLimit: matchPreset.clockLimit,
        clockIncrement: matchPreset.clockIncrement,
      };
    }

    return defaultFormValues;
  }, [games, matchPreset, opponents]);

  useEffect(() => {
    if (matchPreset?.game?.id) {
      setGameId(matchPreset.game.id);
    }
  }, [setGameId, matchPreset?.game?.id]);

  useEffect(() => {
    if (matchPreset) {
      reset(matchToUpdate);
    }

    if (matchPreset?.game?.id !== gameId) {
      reset({ ...defaultValues, ...getValues() });
      setValue('gameId', gameId);
    }

    if (matchPreset?.playersSettings) {
      setValue('playersSettings', matchPreset?.playersSettings);
      setValue('players', getValues().players);
    }
  }, [
    getValues,
    handleAccordionChange,
    reset,
    matchPreset,
    opponents,
    games,
    setValue,
    gameId,
    defaultValues,
    matchToUpdate,
  ]);

  useEffect(() => {
    if (matchPreset) {
      handleAccordionChange(MatchPanelType.MatchSummary);
    }
  }, [handleAccordionChange, matchPreset]);

  const submitHandler = useCallback(() => {
    if (!isPlatformLinked) {
      openPlatformLink();
      return;
    }

    if (!isValid || isValidating) return;

    dispatchUnwrap(createMatch(getValues()), {
      success: (match: MatchEntity) => {
        reset();
        handleAccordionChange(MatchPanelType.GameSelection);
        dispatch(modalsReducers.closeModal(ModalName.CreateMatch));
        navigate(`${Route.MyActiveMatches}?${MATCH_ID_QUERY_KEY}=${match.id}`);
      },
    });
  }, [
    isPlatformLinked,
    isValid,
    isValidating,
    getValues,
    openPlatformLink,
    reset,
    handleAccordionChange,
    dispatch,
    navigate,
  ]);

  const handleChange = useCallback(
    (panel: MatchPanelType) => {
      handleAccordionChange(panel);
    },
    [handleAccordionChange]
  );

  const onCloseHandler = useCallback(() => {
    reset();
    handleAccordionChange(MatchPanelType.GameSelection);
    dispatch(modalsReducers.closeModal(ModalName.CreateMatch));
  }, [handleAccordionChange, dispatch, reset]);

  const matchSetupSettingsSchema = game?.matchesConfiguration?.matchSetupSettings;

  useEffect(() => {
    unregister('matchSetupSettings.*');
    setValue('matchSetupSettings', undefined);

    if (!matchSetupSettingsSchema) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries<any>(matchSetupSettingsSchema.properties).forEach(
      ([propertyName, propertyFields]) => {
        register(`matchSetupSettings.${propertyName}`);

        const value = matchPreset?.matchSetupSettings
          ? matchPreset?.matchSetupSettings[propertyName]
          : propertyFields?.defaultValue;

        if (value !== undefined) {
          setValue(`matchSetupSettings.${propertyName}`, value);
        }
      }
    );
  }, [
    register,
    game,
    unregister,
    matchSetupSettingsSchema,
    setValue,
    matchPreset?.matchSetupSettings,
  ]);

  return (
    <Modal
      maxWidth="md"
      contentHeight={700}
      onClose={onCloseHandler}
      title={MODAL_TITLE}
      name={ModalName.CreateMatch}
      actions={
        <ActionGuard>
          <WithPaidActionButton
            amount={getValues().entryFee}
            label="Create Match"
            onClick={submitHandler}
            isLoading={creating}
          >
            {({ isCanNotEnter, text, onClick }) => (
              <ActionGuard>
                <LoaderButton
                  fullWidth
                  size="medium"
                  color="primary"
                  variant="contained"
                  onClick={onClick}
                  loading={creating}
                  data-testid="create-match-button"
                  disabled={isCanNotEnter}
                >
                  {text}
                </LoaderButton>
              </ActionGuard>
            )}
          </WithPaidActionButton>
        </ActionGuard>
      }
    >
      <FormProvider {...form}>
        <form>
          <Accordion
            title="Game Selection"
            onChange={() => handleChange(MatchPanelType.GameSelection)}
            description={<MatchAccordionDescription text={game?.title} />}
            expanded={expanded === MatchPanelType.GameSelection}
          >
            <GameSelection games={games} value={gameId} onChange={(id) => setGameId(id)} />
          </Accordion>

          <MatchSetupSettingsForm
            isExpanded={expanded === MatchPanelType.MatchSettings}
            onAccordionClick={() => handleChange(MatchPanelType.MatchSettings)}
            game={game}
          />

          <MatchPaymentForm
            isExpanded={expanded === MatchPanelType.Payment}
            onAccordionClick={() => handleChange(MatchPanelType.Payment)}
          />

          <MatchPlayersForm
            isExpanded={expanded === MatchPanelType.TeamSelection}
            onAccordionClick={() => handleChange(MatchPanelType.TeamSelection)}
            opponents={opponents}
            game={game}
          />

          <MatchSummaryForm
            isExpanded={expanded === MatchPanelType.MatchSummary}
            onAccordionClick={() => handleChange(MatchPanelType.MatchSummary)}
          />
        </form>
      </FormProvider>
    </Modal>
  );
};

const CreateMatchModalWrapper: FC = () => {
  const currencyType = MultiCurrencyConfig.currencyType();
  const games = useSelector(gamesSelector.matchGames);
  const [gameId, setGameId] = useState<string>(games[0]?.id ?? '');
  const game = useSelector(gamesSelectorFactory.useById(gameId));

  if (!game || !currencyType) {
    return null;
  }

  return (
    <GameActivityPaymentProvider
      activityType={GameActivityType.Match}
      gameActivity={{
        game: game,
        currencyType,
      }}
    >
      <CreateMatchModalComponent setGameId={setGameId} gameId={gameId} />
    </GameActivityPaymentProvider>
  );
};

export const CreateMatchModal = withModalOpenChecker(
  CreateMatchModalWrapper,
  ModalName.CreateMatch
);
